<ng-helmet>
  <title>Privacy Policy | Uptal</title>
</ng-helmet>
<main-header [darkMode]="true"></main-header>

<!-- English -->
<div class="legal-page pt-28" dir="ltr" *ngIf="!isArabic">
  <div class="hero">
    <span class="eyebrow"
      ><span class="dot" aria-hidden="true"></span> Privacy Policy</span
    >
    <h1>Privacy Policy</h1>
    <p class="lede">
      How Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) collects, processes, stores, discloses, and protects personal
      data across all its services: Talent Search, interviews and assessments
      for employers, and Auto-Apply and CV Enhancer for job seekers.
    </p>
    <div class="meta">
      <span><b>Last updated:</b> July 30, 2026</span>
      <span
        ><b>Applicable law:</b> Personal Data Protection Law (PDPL) of the
        Kingdom of Saudi Arabia, Royal Decree No. M/19, as amended by Royal
        Decree No. M/148</span
      >
      <span
        ><b>Supervisory authority:</b> Saudi Data and Artificial Intelligence
        Authority (SDAIA) / National Data Management Office (NDMO)</span
      >
    </div>
  </div>

  <div class="wrap">
    <nav class="toc" aria-label="On this page">
      <div class="inner">
        <h4>On this page</h4>
        <ol>
          <li><a [routerLink]="[]" fragment="introduction">Introduction</a></li>
          <li>
            <a [routerLink]="[]" fragment="controller">Data controller</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="data-we-collect"
              >Personal data we collect</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="legal-basis"
              >Legal basis for processing</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="purposes">Purposes of processing</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="collection-methods"
              >Data collection methods</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="cookies"
              >Cookies &amp; tracking technologies</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="sharing"
              >Data sharing &amp; disclosure</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="transfers"
              >Cross-border data transfers</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="retention">Data retention</a></li>
          <li><a [routerLink]="[]" fragment="security">Data security</a></li>
          <li>
            <a [routerLink]="[]" fragment="rights"
              >Your rights under the PDPL</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="sensitive"
              >Sensitive personal data</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="children">Children's data</a></li>
          <li>
            <a [routerLink]="[]" fragment="marketing">Direct marketing</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="ai"
              >Automated decision-making &amp; AI</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="thirdparty">Third-party links</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="dpo">Data protection contact</a>
          </li>
          <li><a [routerLink]="[]" fragment="complaints">Complaints</a></li>
          <li><a [routerLink]="[]" fragment="law">Governing law</a></li>
          <li>
            <a [routerLink]="[]" fragment="changes">Changes to this policy</a>
          </li>
        </ol>
      </div>
    </nav>

    <article>
      <section id="introduction">
        <h2><span class="num">1</span> Introduction</h2>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) ("we", "us", "our") is committed to protecting your personal
          data and respecting your privacy in full compliance with the Personal
          Data Protection Law (PDPL) of the Kingdom of Saudi Arabia and its
          Implementing Regulations. This Privacy Policy explains how we collect,
          process, store, disclose, and protect personal data when you use the
          Uptal platform and services, including the recruitment management
          system, Talent Search, AI-powered interviews, assessments, Auto-Apply,
          CV Enhancer, and any other services accessible at
          <a routerLink="/">https://uptal.com</a>.
        </p>
        <p>
          This policy applies to all users of Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), including: subscribers
          (employers, recruiters, and hiring managers); candidates whose
          profiles are accessible through the platform or who participate in
          interviews and assessments; and job seekers who use Auto-Apply or CV
          Enhancer.
        </p>
        <p>
          By accessing or using any Uptal service, you acknowledge that you have
          read, understood, and agree to the practices described in this Privacy
          Policy.
        </p>
      </section>

      <section id="controller">
        <h2><span class="num">2</span> Data controller</h2>
        <p>
          The data controller is Rising Expertise for Information Technology,
          trading as Uptal,
          Commercial Registration No. 1010872769, with its registered office at
          Building 2340, Juaithen Alyazeedi Street, Al Irqah District, Riyadh
          12587, Kingdom of Saudi Arabia.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) is the data controller responsible for determining the purposes
          and means of processing personal data collected through the Uptal
          platform and services.
        </p>
      </section>

      <section id="data-we-collect">
        <h2><span class="num">3</span> Personal data we collect</h2>
        <p>We collect and process the following categories of personal data:</p>

        <h3 class="sub">
          3.1 Subscriber data (employers, recruiters, hiring managers)
        </h3>
        <ul>
          <li>
            <b>Identity data</b>: full name, job title, company name, and
            national ID or commercial registration number where required.
          </li>
          <li>
            <b>Contact data</b>: email address, phone number, and business
            address.
          </li>
          <li>
            <b>Account data</b>: username, password (encrypted), account
            preferences, and subscription plan details.
          </li>
          <li>
            <b>Payment data</b>: billing address, payment method details, and
            transaction history. Payment card data is processed by our
            third-party payment processor and is not stored on Uptal servers.
          </li>
          <li>
            <b>Usage data</b>: search queries, filters applied, profiles viewed,
            profiles unlocked, IP address, browser type, device information,
            session duration, clickstream data, and session recordings and
            interaction data.
          </li>
          <li>
            <b>Service content</b>: interview recordings, candidate resumes, job
            postings, assessment results, and other content you provide when
            using our services.
          </li>
        </ul>

        <h3 class="sub">3.2 Candidate data (Talent Search)</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) provides access to verified candidate profiles sourced from
          publicly available data, direct candidate submissions, and authorized
          partner databases. Through Talent Search, candidate data may include:
        </p>
        <ul>
          <li>
            <b>Identity data</b>: full name, nationality, gender, and city of
            residence.
          </li>
          <li>
            <b>Professional data</b>: job title, employer, years of experience,
            industry, skills, education, certifications, and career history.
          </li>
          <li>
            <b>Compensation data</b>: current salary, expected salary, and
            compensation preferences.
          </li>
          <li>
            <b>Contact data</b>: verified email address and phone number
            (accessible only upon profile unlock by a subscriber).
          </li>
          <li>
            <b>Seniority classification</b>: executive-level indicators for
            C-suite, VP, and director profiles.
          </li>
        </ul>

        <h3 class="sub">3.3 Job seeker data (Auto-Apply and CV Enhancer)</h3>
        <p>When you use Auto-Apply or CV Enhancer, we collect and process:</p>
        <ul>
          <li>
            <b>CV and profile data</b>: your CV and its contents, including
            identity, contact, education, work history, skills, and any other
            information it contains, together with your job preferences (target
            roles, cities, company types, and salary expectations).
          </li>
          <li>
            <b>Application data</b>: the roles matched to you, the tailored
            versions of your CV generated for each role, the applications
            submitted on your behalf, their dates and destinations, and any
            status information we receive.
          </li>
          <li>
            <b>Account and subscription data</b>: username, password
            (encrypted), preferences, subscription tier, and pause or
            cancellation history.
          </li>
          <li>
            <b>Payment data</b>: billing details and transaction history.
            Payment card data is processed by our third-party payment processor
            and is not stored on Uptal servers.
          </li>
          <li>
            <b>Communications data</b>: your correspondence with our support
            team and the notifications we send you.
          </li>
        </ul>

        <h3 class="sub">3.4 Interview and assessment data</h3>
        <p>
          When candidates participate in interviews or assessments through the
          Uptal platform, we may additionally collect:
        </p>
        <ul>
          <li>
            <b>Interview recordings</b>: video and audio recordings of interview
            sessions.
          </li>
          <li>
            <b>Assessment responses</b>: answers to technical, psychometric, or
            competency-based assessments.
          </li>
          <li>
            <b>AI-generated analysis</b>: transcripts, evaluation scores, and
            performance summaries generated by our AI systems.
          </li>
        </ul>
        <p>
          The categories described above are collected at the level of
          granularity necessary for the relevant service. By way of illustration
          and not limitation: identification and contact data comprise name,
          email address, telephone number, and, where provided, national address
          details; professional data comprise CV content, employment history,
          educational qualifications, certifications, skills, languages, salary
          expectations, and role preferences; interview and assessment data
          comprise recordings, transcripts, responses, scores, and evaluator or
          system-generated annotations; technical data comprise device
          identifiers, browser type and version, operating system, IP address,
          log records, and session metadata; and transactional data comprise
          plan, order, invoicing, and payment-status information (excluding full
          card numbers, which are processed by our payment processor and are not
          stored on Uptal servers). We do not knowingly collect personal data
          that is not required for the purposes described in this Policy, and
          where optional fields are presented, their completion is voluntary.
        </p>
      </section>

      <section id="legal-basis">
        <h2><span class="num">4</span> Legal basis for processing</h2>
        <p>
          In accordance with Article 10 of the PDPL, we process personal data
          based on one or more of the following lawful bases:
        </p>
        <ul>
          <li>
            <b>Consent</b>: where you have given explicit consent for the
            processing of your personal data for one or more specific purposes.
            You may withdraw your consent at any time by contacting us.
          </li>
          <li>
            <b>Contractual necessity</b>: where processing is necessary for the
            performance of a subscription or service agreement to which you are
            a party, including providing access to the platform, matching and
            submitting job applications on your behalf (Auto-Apply), producing
            your enhanced CV (CV Enhancer), and processing payments.
          </li>
          <li>
            <b>Legitimate interest</b>: where processing is necessary for our
            legitimate business interests (for example, fraud prevention,
            platform security, and service improvement), provided such interests
            do not override your rights and freedoms. This basis does not apply
            to the processing of sensitive data.
          </li>
          <li>
            <b>Legal obligation</b>: where processing is necessary to comply
            with applicable laws and regulations of the Kingdom of Saudi Arabia,
            including tax, anti-money laundering, and regulatory reporting
            requirements.
          </li>
          <li>
            <b>Public interest</b>: where processing is necessary for purposes
            of public interest as defined under the PDPL.
          </li>
        </ul>
        <p>
          For each processing activity, we identify and record the applicable
          legal basis before processing begins, and we rely on one basis per
          purpose as documented in our records of processing activities. Where
          processing is based on your consent, that consent is requested
          separately for each distinct purpose, is evidenced and logged, and may
          be withdrawn at any time with effect for the future through your
          account settings or by contacting us; withdrawal of consent does not
          affect the lawfulness of processing carried out before withdrawal, and
          where consent is withdrawn we cease the relevant processing unless and
          to the extent another legal basis under the PDPL applies and we inform
          you accordingly.
        </p>
      </section>

      <section id="purposes">
        <h2><span class="num">5</span> Purposes of processing</h2>
        <p>
          We process personal data for the following specific, clear, and
          explicit purposes:
        </p>
        <ul>
          <li>
            Providing and operating the Uptal platform and its services,
            including Talent Search, interviews, assessments, recruitment
            management, Auto-Apply, and CV Enhancer
          </li>
          <li>Creating and managing user accounts</li>
          <li>Processing payments and issuing invoices</li>
          <li>
            Enabling candidate profile search, filtering, ranking, and unlocking
          </li>
          <li>Delivering AI-powered candidate matching and ranking results</li>
          <li>
            Matching job seekers to suitable roles, generating tailored versions
            of their CVs, and submitting job applications on their behalf
            (Auto-Apply)
          </li>
          <li>
            Including job seeker profiles and CVs, including CVs uploaded to
            preview or check the job matches available, in Uptal's candidate
            database and talent pool, and making them available and discoverable
            to employers and recruiters through Talent Search and other current
            or future Uptal services
          </li>
          <li>Enhancing and rewriting job seekers' CVs (CV Enhancer)</li>
          <li>Analyzing and evaluating candidate interviews and assessments</li>
          <li>Providing hiring recommendations and AI-generated insights</li>
          <li>Personalizing your experience with Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)</li>
          <li>
            Communicating with users about their accounts, billing,
            applications, and service updates
          </li>
          <li>
            Sending marketing communications (with opt-out available at any
            time)
          </li>
          <li>
            Analyzing platform usage to improve our services, features, and user
            experience
          </li>
          <li>
            Preventing fraud, unauthorized access, and ensuring platform
            security
          </li>
          <li>
            Complying with legal, regulatory, and governmental obligations under
            KSA law
          </li>
          <li>
            Responding to data subject requests in accordance with the PDPL
          </li>
        </ul>
        <p>
          We process personal data only for the purposes set out above and for
          purposes compatible with them, and we do not further process personal
          data in a manner incompatible with those purposes without first
          providing you with notice and, where required by the PDPL, obtaining
          your consent or identifying another lawful basis. Where we intend to
          process personal data for a new purpose, we will assess the
          relationship between the original and the new purpose, the context of
          collection, the nature of the data, the possible consequences for you,
          and the existence of appropriate safeguards before commencing such
          processing.
        </p>
      </section>

      <section id="collection-methods">
        <h2><span class="num">6</span> Data collection methods</h2>
        <ul>
          <li>
            <b>Direct collection</b>: information you provide when creating an
            account, uploading a CV, setting job preferences, subscribing to a
            plan, placing a CV Enhancer order, or contacting our support team.
          </li>
          <li>
            <b>Automated collection</b>: usage data, cookies, and similar
            tracking technologies collected automatically when you interact with
            the platform.
          </li>
          <li>
            <b>Generated data</b>: data our systems create in the course of
            providing the services, such as tailored CVs, match results, and AI
            evaluations.
          </li>
          <li>
            <b>Third-party sources</b>: candidate profile data sourced from
            publicly available professional databases, authorized data partners,
            and direct candidate submissions.
          </li>
        </ul>
        <p>
          Where personal data is collected indirectly (not directly from the
          data subject), we take necessary measures to inform the data subject
          within thirty (30) days or at the time of first contact, whichever is
          earlier, in accordance with the PDPL Implementing Regulations.
        </p>
      </section>

      <section id="cookies">
        <h2><span class="num">7</span> Cookies &amp; tracking technologies</h2>
        <p>
          We use cookies and similar technologies to enhance your experience and
          analyze platform usage. These include:
        </p>
        <ul>
          <li>
            <b>Essential cookies</b>: required for platform functionality,
            authentication, and security. These cannot be disabled.
          </li>
          <li>
            <b>Analytics cookies</b>: used to understand how visitors interact
            with the platform, helping us improve performance and user
            experience.
          </li>
          <li>
            <b>Marketing cookies</b>: used to deliver relevant advertisements
            and measure campaign effectiveness. These are only placed with your
            consent.
          </li>
        </ul>
        <p>
          You may manage your cookie preferences through your browser settings
          or the cookie consent banner displayed on the platform. Disabling
          certain cookies may affect platform functionality.
        </p>
      </section>

      <section id="sharing">
        <h2><span class="num">8</span> Data sharing &amp; disclosure</h2>
        <div class="callout">We do not sell personal data.</div>
        <p>
          We may share personal data with the following categories of
          recipients, solely for the purposes described in this policy:
        </p>
        <ul>
          <li>
            <b>Employers and job platforms (Auto-Apply)</b>: when Auto-Apply
            submits an application on your behalf, your application details and
            tailored CV, including the personal data they contain, are disclosed
            to the prospective employer or the job platform hosting the role.
            This disclosure is the core function of the service you subscribe
            to.
          </li>
          <li>
            <b>Subscribers (Talent Search)</b>: candidate contact data (email
            and phone number) is disclosed to subscribers only upon profile
            unlock, subject to the subscriber's plan allowance. Job seeker
            profiles and CVs submitted through any Uptal service, including CVs
            uploaded to preview or check available job matches, may be included
            in the candidate database that is searchable by, and offered to,
            Subscribers, with contact data disclosed only upon profile unlock.
          </li>
          <li>
            <b>Service providers</b>: third-party providers who assist with
            payment processing, cloud hosting, analytics, email delivery,
            messaging, and customer support. All service providers are
            contractually bound to protect personal data in accordance with the
            PDPL.
          </li>
          <li>
            <b>Partners and affiliates</b>: we share personal data with our
            partners and affiliates for marketing purposes only with your prior
            consent. You may withdraw that consent at any time.
          </li>
          <li>
            <b>Legal and regulatory authorities</b>: where disclosure is
            required by law, regulation, court order, or governmental request
            issued by competent authorities in the Kingdom of Saudi Arabia.
          </li>
          <li>
            <b>Business transfers</b>: in connection with a merger, acquisition,
            or sale of all or substantially all of our assets, subject to the
            acquiring entity's obligation to honor this Privacy Policy.
          </li>
        </ul>
        <p>
          Where the PDPL restricts disclosure, for example where disclosure
          would threaten national security or the safety of individuals, we
          apply those restrictions.
        </p>
        <p>
          All service providers and processors that process personal data on our
          behalf do so under written agreements that impose obligations of
          confidentiality, security, purpose limitation, and compliance with the
          PDPL, that restrict processing to our documented instructions, and
          that require the return or destruction of personal data upon
          termination of the engagement. We do not sell personal data, and we do
          not permit our processors to use personal data for their own
          independent purposes. Disclosures to employers and recruiters through
          the Services are limited to the data made available through the
          relevant product features and are subject to the recipient's own
          obligations under applicable data protection law.
        </p>
      </section>

      <section id="transfers">
        <h2><span class="num">9</span> Cross-border data transfers</h2>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) stores and processes personal data on secure cloud
          infrastructure operated by leading cloud providers. Where personal
          data is stored, processed, or accessed outside the Kingdom of Saudi
          Arabia, or is otherwise transferred outside the Kingdom (including
          where an Auto-Apply application is submitted to an employer located
          outside the Kingdom at your direction), we ensure that:
        </p>
        <ul>
          <li>
            The transfer serves a specific and legitimate purpose as permitted
            under the PDPL.
          </li>
          <li>
            The recipient country or entity provides an adequate level of
            personal data protection, as assessed by SDAIA, or appropriate
            safeguards are in place.
          </li>
          <li>
            Appropriate transfer mechanisms are used, including Standard
            Contractual Clauses (SCCs) or Binding Corporate Rules (BCRs) where
            required.
          </li>
          <li>
            The protections afforded under the PDPL continue to apply to the
            transferred data.
          </li>
        </ul>
        <p>
          Before any cross-border transfer, we assess and document the necessity
          of the transfer, the categories of data concerned, the destination,
          and the safeguards applied, and we transfer only the minimum personal
          data necessary to achieve the transfer purpose. Where SDAIA issues,
          amends, or withdraws adequacy determinations, transfer rules, or
          guidance, we align our transfer practices with the applicable
          requirements as in force at the time of the transfer.
        </p>
      </section>

      <section id="retention">
        <h2><span class="num">10</span> Data retention</h2>
        <p>
          We retain personal data only for as long as necessary to fulfill the
          purposes for which it was collected, or as required by applicable law.
          Our retention practices are as follows:
        </p>
        <ul>
          <li>
            <b>Account data (subscribers and job seekers)</b>: retained for the
            duration of the active account or subscription and for five (5)
            years following account closure, as required for record-keeping and
            legal compliance.
          </li>
          <li>
            <b>Payment and transaction data</b>: retained for the minimum period
            required by Saudi Arabian tax and commercial law.
          </li>
          <li>
            <b>Candidate profile data</b>: retained as long as the data remains
            relevant and accurate, subject to periodic review. Candidates may
            request deletion at any time.
          </li>
          <li>
            <b
              >CV, application, and tailored-CV data (Auto-Apply and CV
              Enhancer)</b
            >: retained for the duration of your account and as needed to
            evidence the applications submitted on your behalf; you may request
            deletion at any time, subject to legal record-keeping requirements.
            Copies of applications already delivered to employers are held by
            those employers under their own policies.
          </li>
          <li>
            <b>Usage and analytics data</b>: retained in anonymized or
            aggregated form for service improvement purposes.
          </li>
          <li>
            <b>Records of processing activities</b>: maintained for the duration
            of processing activities and for an additional five (5) years
            thereafter, in accordance with the PDPL Implementing Regulations.
          </li>
        </ul>
        <p>
          When personal data is no longer needed, we securely destroy or
          anonymize it such that re-identification of the data subject is not
          possible.
        </p>
        <p>
          In determining retention periods, we apply the following criteria: the
          duration of the contractual relationship and any applicable warranty,
          dispute, or limitation periods; statutory retention obligations under
          KSA law, including tax, commercial, and regulatory record-keeping
          requirements; the legitimate operational need for the data, including
          security, audit, and fraud-prevention purposes; and the nature and
          sensitivity of the data concerned. Where data is retained for
          different purposes with different periods, the data is retained only
          as long as the longest applicable period and is restricted to the
          purposes justifying that period.
        </p>
      </section>

      <section id="security">
        <h2><span class="num">11</span> Data security</h2>
        <p>
          We implement appropriate technical and organizational measures to
          protect personal data against unauthorized access, alteration,
          disclosure, or destruction. These measures include, but are not
          limited to:
        </p>
        <ul>
          <li>Encryption of personal data in transit (TLS/SSL) and at rest</li>
          <li>
            Hardened, access-controlled cloud infrastructure operated by leading
            cloud providers
          </li>
          <li>
            Role-based access controls and multi-factor authentication for
            internal systems
          </li>
          <li>
            Regular security assessments, vulnerability scanning, and
            penetration testing
          </li>
          <li>
            Employee data protection training and confidentiality agreements
          </li>
          <li>
            Incident response procedures aligned with NDMO and SDAIA guidelines
          </li>
          <li>NDMO-aligned data governance framework</li>
        </ul>
        <p>
          While we take commercially reasonable precautions, no method of
          electronic transmission or storage is completely secure. In the event
          of a personal data breach, we will notify SDAIA within seventy-two
          (72) hours and inform affected data subjects without undue delay, as
          required by the PDPL.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your
          account credentials, for using appropriately secured devices and
          networks when accessing the Services, and for promptly notifying us of
          any suspected compromise of your account. Security measures are
          reviewed periodically and adjusted in light of evolving threats,
          technological developments, and regulatory guidance; however, no set
          of measures can guarantee absolute security, and transmissions to and
          from the Services are made at your own initiative.
        </p>
      </section>

      <section id="rights">
        <h2><span class="num">12</span> Your rights under the PDPL</h2>
        <p>
          In accordance with the PDPL and its Implementing Regulations, you have
          the following rights with respect to your personal data:
        </p>
        <ul>
          <li>
            <b>Right to be informed</b>: to know our identity, the purpose for
            collecting your data, the methods of collection, and with whom your
            data may be shared.
          </li>
          <li>
            <b>Right of access</b>: to request access to the personal data we
            hold about you and to receive a copy of such data.
          </li>
          <li>
            <b>Right to correction</b>: to request correction of any inaccurate,
            incomplete, or outdated personal data.
          </li>
          <li>
            <b>Right to destruction</b>: to request the destruction of your
            personal data when it is no longer necessary for the purpose for
            which it was collected, or when you withdraw your consent.
          </li>
          <li>
            <b>Right to withdraw consent</b>: to withdraw your consent to
            processing at any time. Withdrawal does not affect the lawfulness of
            processing carried out prior to withdrawal, and it does not recall
            applications already submitted to employers on your instructions.
          </li>
          <li>
            <b>Right to restrict processing</b>: to request limitation of
            processing in certain circumstances and for a limited duration.
          </li>
          <li>
            <b>Right to data portability</b>: to obtain your personal data in a
            structured, commonly used, machine-readable format, and to request
            transmission to another controller.
          </li>
          <li>
            <b>Right to object</b>: to object to processing based on legitimate
            interest.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at
          <a href="mailto:support@uptal.com" class="mail">support@uptal.com</a>
          or through <a routerLink="/contact-us">uptal.com/contact-us</a>. We
          will respond within thirty (30) calendar days. In complex cases, this
          period may be extended by an additional thirty (30) days, and we will
          notify you of the extension and the reason for it. We may request
          identity verification before processing your request.
        </p>
        <p>
          Exercising your rights is free of charge, save that, where requests
          are manifestly unfounded, excessive, or repetitive, we may charge a
          reasonable administrative fee or decline to act on the request, in
          each case to the extent permitted by the PDPL, and we will explain the
          reason for any such decision. Certain rights are subject to conditions
          and exemptions under the PDPL and its Implementing Regulations,
          including where fulfilling a request would prejudice the rights of
          others, conflict with a legal obligation, or concern data processed
          for purposes exempted by law; where we rely on such a condition or
          exemption, we will inform you unless prohibited from doing so. If you
          are dissatisfied with our handling of a request, you may escalate the
          matter to our data protection contact identified in this Policy and,
          without prejudice to that, lodge a complaint with the competent
          authority as described in the Complaints section.
        </p>
      </section>

      <section id="sensitive">
        <h2><span class="num">13</span> Sensitive personal data</h2>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not intentionally collect or process sensitive personal
          data as defined under the PDPL, which includes health data, biometric
          data, genetic data, data revealing racial or ethnic origin, or
          criminal records. If your CV contains such information, we recommend
          removing it before upload. If we become aware that sensitive data has
          been inadvertently collected, we will promptly take steps to delete it
          or obtain the required explicit consent.
        </p>
      </section>

      <section id="children">
        <h2><span class="num">14</span> Children's data</h2>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) is not intended for individuals under the age of eighteen (18).
          We do not knowingly collect personal data from minors. If we become
          aware that we have collected data from a minor, we will take immediate
          steps to delete such data.
        </p>
      </section>

      <section id="marketing">
        <h2><span class="num">15</span> Direct marketing</h2>
        <p>
          We may use your contact data to send you marketing communications
          about Uptal products, features, and promotions. You may opt out at any
          time by:
        </p>
        <ul>
          <li>Clicking the unsubscribe link in any marketing email</li>
          <li>
            Contacting us at
            <a href="mailto:support@uptal.com" class="mail"
              >support@uptal.com</a
            >
          </li>
          <li>
            Updating your communication preferences in your account settings
          </li>
        </ul>
        <p>
          Opting out of marketing communications does not affect transactional
          or service-related communications (for example, billing notices,
          application updates, security alerts, and subscription confirmations).
        </p>
      </section>

      <section id="ai">
        <h2><span class="num">16</span> Automated decision-making &amp; AI</h2>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) uses artificial intelligence and automated processing across its
          services, including:
        </p>
        <ul>
          <li>
            Ranking and matching candidate profiles in Talent Search based on
            criteria such as skill match, salary fit, seniority level, and role
            relevance
          </li>
          <li>
            Matching job seekers to roles in Auto-Apply based on their CV and
            the preferences they set, generating tailored versions of their CV
            for each role, and submitting applications on their behalf
          </li>
          <li>
            Analyzing and evaluating candidate interview recordings and scoring
            assessment responses
          </li>
          <li>Generating hiring recommendations and AI-generated insights</li>
        </ul>
        <p>
          These automated processes assist recruiters in identifying suitable
          candidates and assist job seekers in applying at scale. Automated
          results, including candidate rankings, interview evaluations,
          assessment scores, and role matches, are recommendations only and do
          not constitute binding decisions. AI-generated outputs are
          probabilistic estimates and may contain errors or inaccuracies; they
          are not guaranteed to be accurate, complete, or current. Hiring
          decisions remain solely with employers. In Auto-Apply, you control the
          targeting preferences that drive matching, and you can review your
          application history, adjust preferences, or pause the service at any
          time. Applications are submitted under your express authorization and
          cannot be retracted once transmitted; pausing takes effect on future
          submissions only. If you believe an automated process has adversely
          affected your rights, you may contact us to request a review.
        </p>
      </section>

      <section id="thirdparty">
        <h2><span class="num">17</span> Third-party links</h2>
        <p>
          The Uptal platform may contain links to third-party websites or
          services, including employer sites and job platforms to which
          applications are submitted. We are not responsible for the privacy
          practices or content of these third-party sites. We encourage you to
          review the privacy policies of any third-party service you interact
          with.
        </p>
      </section>

      <section id="dpo">
        <h2><span class="num">18</span> Data protection contact</h2>
        <p>
          In accordance with PDPL requirements, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has designated a point of
          contact for data protection matters. For any questions, concerns, or
          requests related to this Privacy Policy or the processing of your
          personal data, please contact us at
          <a href="mailto:support@uptal.com" class="mail">support@uptal.com</a>.
        </p>
      </section>

      <section id="complaints">
        <h2><span class="num">19</span> Complaints</h2>
        <p>
          If you believe that your personal data has been processed in violation
          of the PDPL, you have the right to file a complaint with:
        </p>
        <ul>
          <li>
            <b>Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)</b>: by contacting us at
            <a href="mailto:support@uptal.com" class="mail">support@uptal.com</a
            >. We will investigate and respond within thirty (30) days.
          </li>
          <li>
            <b>SDAIA</b>: the Saudi Data and Artificial Intelligence Authority,
            the competent supervisory authority for data protection matters in
            the Kingdom of Saudi Arabia. Complaints may be submitted through the
            National Data Governance Platform (NDGP).
          </li>
        </ul>
      </section>

      <section id="law">
        <h2><span class="num">20</span> Governing law</h2>
        <p>
          This Privacy Policy is governed by and construed in accordance with
          the laws of the Kingdom of Saudi Arabia, including the Personal Data
          Protection Law (PDPL) and its Implementing Regulations. Any disputes
          arising under this policy shall be subject to the exclusive
          jurisdiction of the competent courts in Riyadh, Saudi Arabia.
        </p>
      </section>

      <section id="changes">
        <h2><span class="num">21</span> Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          in our practices, technology, legal requirements, or business
          operations. When we make material changes, we will:
        </p>
        <ul>
          <li>
            Post the updated policy on our website with a revised effective date
          </li>
          <li>Notify active users via email of significant changes</li>
          <li>
            Where required by the PDPL, obtain your consent before applying
            material changes that affect how your personal data is processed
          </li>
        </ul>
        <p>
          We encourage you to review this Privacy Policy periodically. Except
          where the PDPL requires us to obtain your consent, your continued use
          of Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) after any changes constitutes acceptance of the revised
          policy.
        </p>
        <p class="lastline">
          This Privacy Policy was last updated on July 30, 2026.
        </p>
      </section>
    </article>
  </div>
</div>

<!-- Arabic -->
<div class="legal-page pt-28" dir="rtl" lang="ar" *ngIf="isArabic">
  <div class="hero">
    <span class="eyebrow"
      ><span class="dot" aria-hidden="true"></span> سياسة الخصوصية</span
    >
    <h1>سياسة الخصوصية</h1>
    <p class="lede">
      كيف تجمع شركة الخبرات الصاعدة لتقنية المعلومات البيانات الشخصية وتعالجها وتخزّنها وتفصح عنها وتحميها عبر
      جميع خدماتها: البحث عن الكفاءات والمقابلات والتقييمات لأصحاب العمل،
      والتقديم التلقائي وتحسين السيرة الذاتية للباحثين عن عمل.
    </p>
    <div class="meta">
      <span><b>آخر تحديث:</b> 30 يوليو 2026</span>
      <span
        ><b>النظام المطبَّق:</b> نظام حماية البيانات الشخصية في المملكة العربية
        السعودية، الصادر بالمرسوم الملكي رقم م/19 والمعدَّل بالمرسوم الملكي رقم
        م/148</span
      >
      <span
        ><b>الجهة الإشرافية:</b> الهيئة السعودية للبيانات والذكاء الاصطناعي
        (سدايا) / مكتب إدارة البيانات الوطنية</span
      >
    </div>
  </div>

  <div class="wrap">
    <nav class="toc" aria-label="في هذه الصفحة">
      <div class="inner">
        <h4>في هذه الصفحة</h4>
        <ol>
          <li><a [routerLink]="[]" fragment="introduction">مقدمة</a></li>
          <li>
            <a [routerLink]="[]" fragment="controller">جهة التحكم بالبيانات</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="data-we-collect"
              >البيانات الشخصية التي نجمعها</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="legal-basis"
              >الأساس النظامي للمعالجة</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="purposes">أغراض المعالجة</a></li>
          <li>
            <a [routerLink]="[]" fragment="collection-methods"
              >طرق جمع البيانات</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="cookies"
              >ملفات تعريف الارتباط وتقنيات التتبع</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="sharing"
              >مشاركة البيانات والإفصاح عنها</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="transfers"
              >نقل البيانات خارج المملكة</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="retention">الاحتفاظ بالبيانات</a>
          </li>
          <li><a [routerLink]="[]" fragment="security">أمن البيانات</a></li>
          <li>
            <a [routerLink]="[]" fragment="rights"
              >حقوقك بموجب نظام حماية البيانات الشخصية</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="sensitive"
              >البيانات الشخصية الحساسة</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="children">بيانات القاصرين</a></li>
          <li><a [routerLink]="[]" fragment="marketing">التسويق المباشر</a></li>
          <li>
            <a [routerLink]="[]" fragment="ai"
              >القرارات الآلية والذكاء الاصطناعي</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="thirdparty">روابط الأطراف الثالثة</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="dpo">جهة التواصل لحماية البيانات</a>
          </li>
          <li><a [routerLink]="[]" fragment="complaints">الشكاوى</a></li>
          <li><a [routerLink]="[]" fragment="law">القانون الحاكم</a></li>
          <li>
            <a [routerLink]="[]" fragment="changes"
              >التغييرات على هذه السياسة</a
            >
          </li>
        </ol>
      </div>
    </nav>

    <article>
      <section id="introduction">
        <h2><span class="num">1</span> مقدمة</h2>
        <p>
          تلتزم شركة الخبرات الصاعدة لتقنية المعلومات ("نحن" أو "لنا") بحماية بياناتك الشخصية واحترام خصوصيتك
          بما يتوافق تمامًا مع نظام حماية البيانات الشخصية في المملكة العربية
          السعودية ولوائحه التنفيذية. وتوضّح سياسة الخصوصية هذه كيفية جمعنا
          للبيانات الشخصية ومعالجتها وتخزينها والإفصاح عنها وحمايتها عند
          استخدامك منصة شركة الخبرات الصاعدة لتقنية المعلومات وخدماتها، بما في ذلك نظام إدارة التوظيف والبحث عن
          الكفاءات والمقابلات المدعومة بالذكاء الاصطناعي والتقييمات والتقديم
          التلقائي وتحسين السيرة الذاتية وأي خدمات أخرى متاحة عبر
          <a routerLink="/" class="en">https://uptal.com</a>.
        </p>
        <p>
          تنطبق هذه السياسة على جميع مستخدمي شركة الخبرات الصاعدة لتقنية المعلومات، بمن فيهم: المشتركون (أصحاب
          العمل وجهات التوظيف ومسؤولو التوظيف)؛ والمرشحون الذين تكون ملفاتهم
          متاحة عبر المنصة أو يشاركون في المقابلات والتقييمات؛ والباحثون عن عمل
          الذين يستخدمون التقديم التلقائي أو تحسين السيرة الذاتية.
        </p>
        <p>
          وبوصولك إلى أي خدمة من خدمات شركة الخبرات الصاعدة لتقنية المعلومات أو استخدامك لها، فإنك تقرّ بأنك
          قرأت الممارسات الموضّحة في سياسة الخصوصية هذه وفهمتها ووافقت عليها.
        </p>
      </section>

      <section id="controller">
        <h2><span class="num">2</span> جهة التحكم بالبيانات</h2>
        <p>
          جهة التحكم بالبيانات هي شركة الخبرات الصاعدة لتقنية المعلومات، وتعمل
          تحت الاسم التجاري "أبتال"،
          سجل تجاري رقم 1010872769، ومقرها المسجَّل: مبنى 2340، شارع جعيثن
          اليزيدي، حي عرقة، الرياض 12587، المملكة العربية السعودية.
        </p>
        <p>
          شركة الخبرات الصاعدة لتقنية المعلومات هي جهة التحكم المسؤولة عن تحديد أغراض ووسائل معالجة البيانات
          الشخصية المجموعة عبر منصة شركة الخبرات الصاعدة لتقنية المعلومات وخدماتها.
        </p>
      </section>

      <section id="data-we-collect">
        <h2><span class="num">3</span> البيانات الشخصية التي نجمعها</h2>
        <p>نجمع ونعالج فئات البيانات الشخصية التالية:</p>

        <h3 class="sub">
          <span class="n">3.1</span> بيانات المشتركين (أصحاب العمل وجهات التوظيف
          ومسؤولو التوظيف)
        </h3>
        <ul>
          <li>
            <b>بيانات الهوية</b>: الاسم الكامل والمسمى الوظيفي واسم الشركة ورقم
            الهوية الوطنية أو السجل التجاري حيثما يُطلَب.
          </li>
          <li>
            <b>بيانات التواصل</b>: البريد الإلكتروني ورقم الهاتف وعنوان العمل.
          </li>
          <li>
            <b>بيانات الحساب</b>: اسم المستخدم وكلمة المرور (مشفَّرة) وتفضيلات
            الحساب وتفاصيل باقة الاشتراك.
          </li>
          <li>
            <b>بيانات الدفع</b>: عنوان الفوترة وتفاصيل وسيلة الدفع وسجل
            المعاملات. وتُعالَج بيانات بطاقات الدفع بواسطة معالج الدفع التابع
            لطرف ثالث ولا تُخزَّن على خوادم شركة الخبرات الصاعدة لتقنية المعلومات.
          </li>
          <li>
            <b>بيانات الاستخدام</b>: استعلامات البحث والمرشحات المطبَّقة
            والملفات المعروضة والملفات المفتوحة وعنوان
            <span class="en">IP</span> ونوع المتصفح ومعلومات الجهاز ومدة الجلسة
            وبيانات التصفح وتسجيلات الجلسات وبيانات التفاعل.
          </li>
          <li>
            <b>محتوى الخدمة</b>: تسجيلات المقابلات والسير الذاتية للمرشحين
            والإعلانات الوظيفية ونتائج التقييمات وأي محتوى آخر تقدّمه عند
            استخدام خدماتنا.
          </li>
        </ul>

        <h3 class="sub">
          <span class="n">3.2</span> بيانات المرشحين (البحث عن الكفاءات)
        </h3>
        <p>
          توفّر شركة الخبرات الصاعدة لتقنية المعلومات وصولاً إلى ملفات مرشحين موثَّقة مستمدة من بيانات متاحة
          للعامة ومن تقديمات المرشحين المباشرة ومن قواعد بيانات شركاء معتمَدين.
          وعبر البحث عن الكفاءات، قد تشمل بيانات المرشح:
        </p>
        <ul>
          <li>
            <b>بيانات الهوية</b>: الاسم الكامل والجنسية والجنس ومدينة الإقامة.
          </li>
          <li>
            <b>البيانات المهنية</b>: المسمى الوظيفي وجهة العمل وسنوات الخبرة
            والقطاع والمهارات والتعليم والشهادات والمسار المهني.
          </li>
          <li>
            <b>بيانات الأجور</b>: الراتب الحالي والراتب المتوقع وتفضيلات الأجور.
          </li>
          <li>
            <b>بيانات التواصل</b>: البريد الإلكتروني ورقم الهاتف الموثَّقان (لا
            يمكن الوصول إليهما إلا عند فتح الملف من قبل مشترك).
          </li>
          <li>
            <b>تصنيف الأقدمية</b>: مؤشرات المستوى التنفيذي لملفات الإدارة العليا
            ونواب الرؤساء والمديرين.
          </li>
        </ul>

        <h3 class="sub">
          <span class="n">3.3</span> بيانات الباحثين عن عمل (التقديم التلقائي
          وتحسين السيرة الذاتية)
        </h3>
        <p>
          عند استخدامك التقديم التلقائي أو تحسين السيرة الذاتية، نجمع ونعالج:
        </p>
        <ul>
          <li>
            <b>بيانات السيرة الذاتية والملف</b>: سيرتك الذاتية ومحتوياتها، بما
            فيها بيانات الهوية والتواصل والتعليم والتاريخ الوظيفي والمهارات وأي
            معلومات أخرى تتضمنها، إضافةً إلى تفضيلاتك الوظيفية (الأدوار
            المستهدفة والمدن وأنواع الشركات وتوقعات الراتب).
          </li>
          <li>
            <b>بيانات الطلبات</b>: الوظائف المطابَقة لك والنسخ المخصَّصة من
            سيرتك الذاتية المولَّدة لكل وظيفة والطلبات المقدَّمة نيابةً عنك
            وتواريخها ووجهاتها وأي معلومات حالة نتلقاها.
          </li>
          <li>
            <b>بيانات الحساب والاشتراك</b>: اسم المستخدم وكلمة المرور (مشفَّرة)
            والتفضيلات وفئة الاشتراك وسجل الإيقاف المؤقت أو الإلغاء.
          </li>
          <li>
            <b>بيانات الدفع</b>: تفاصيل الفوترة وسجل المعاملات. وتُعالَج بيانات
            بطاقات الدفع بواسطة معالج الدفع التابع لطرف ثالث ولا تُخزَّن على
            خوادم شركة الخبرات الصاعدة لتقنية المعلومات.
          </li>
          <li>
            <b>بيانات الاتصالات</b>: مراسلاتك مع فريق الدعم والإشعارات التي
            نرسلها إليك.
          </li>
        </ul>

        <h3 class="sub">
          <span class="n">3.4</span> بيانات المقابلات والتقييمات
        </h3>
        <p>
          عند مشاركة المرشحين في مقابلات أو تقييمات عبر منصة شركة الخبرات الصاعدة لتقنية المعلومات، قد نجمع
          إضافةً إلى ذلك:
        </p>
        <ul>
          <li>
            <b>تسجيلات المقابلات</b>: تسجيلات الفيديو والصوت لجلسات المقابلات.
          </li>
          <li>
            <b>إجابات التقييمات</b>: الإجابات على التقييمات التقنية أو النفسية
            القياسية (السيكومترية) أو تقييمات الجدارات.
          </li>
          <li>
            <b>التحليلات المولَّدة بالذكاء الاصطناعي</b>: النصوص المفرَّغة
            ودرجات التقييم وملخصات الأداء المولَّدة بأنظمة الذكاء الاصطناعي
            لدينا.
          </li>
        </ul>
        <p>
          تُجمَع الفئات الموضحة أعلاه بمستوى التفصيل اللازم للخدمة المعنية. وعلى
          سبيل المثال لا الحصر: تشمل بيانات الهوية والتواصل الاسم والبريد
          الإلكتروني ورقم الهاتف، وتفاصيل العنوان الوطني حيثما قُدِّمت؛ وتشمل
          البيانات المهنية محتوى السيرة الذاتية والتاريخ الوظيفي والمؤهلات
          التعليمية والشهادات والمهارات واللغات وتوقعات الراتب وتفضيلات الأدوار؛
          وتشمل بيانات المقابلات والتقييمات التسجيلات والنصوص المفرَّغة
          والإجابات والدرجات والملاحظات المولَّدة من المقيّمين أو من النظام؛
          وتشمل البيانات التقنية معرّفات الجهاز ونوع المتصفح وإصداره ونظام
          التشغيل وعنوان <span class="en">IP</span> وسجلات النظام وبيانات
          الجلسات؛ وتشمل بيانات المعاملات معلومات الباقة والطلب والفوترة وحالة
          الدفع (باستثناء أرقام البطاقات الكاملة التي يعالجها معالج الدفع لدينا
          ولا تُخزَّن على خوادم شركة الخبرات الصاعدة لتقنية المعلومات). ولا نجمع عن علم بيانات شخصية لا تقتضيها
          الأغراض الموضحة في هذه السياسة، وحيثما تُعرَض حقول اختيارية، يكون
          استكمالها طوعيًا.
        </p>
      </section>

      <section id="legal-basis">
        <h2><span class="num">4</span> الأساس النظامي للمعالجة</h2>
        <p>
          وفقًا للمادة العاشرة من نظام حماية البيانات الشخصية، نعالج البيانات
          الشخصية استنادًا إلى أساس أو أكثر من الأسس النظامية التالية:
        </p>
        <ul>
          <li>
            <b>الموافقة</b>: حيثما منحت موافقة صريحة على معالجة بياناتك الشخصية
            لغرض محدّد أو أكثر. ويمكنك سحب موافقتك في أي وقت بالتواصل معنا.
          </li>
          <li>
            <b>الضرورة التعاقدية</b>: حيثما تكون المعالجة ضرورية لتنفيذ اتفاقية
            اشتراك أو خدمة تكون طرفًا فيها، بما في ذلك إتاحة الوصول إلى المنصة،
            ومطابقة طلبات التوظيف وتقديمها نيابةً عنك (التقديم التلقائي)، وإنتاج
            سيرتك الذاتية المُحسّنة (تحسين السيرة الذاتية)، ومعالجة المدفوعات.
          </li>
          <li>
            <b>المصلحة المشروعة</b>: حيثما تكون المعالجة ضرورية لمصالحنا
            التجارية المشروعة (مثل منع الاحتيال وأمن المنصة وتحسين الخدمة)،
            شريطة ألا تتجاوز تلك المصالح حقوقك وحرياتك. ولا ينطبق هذا الأساس على
            معالجة البيانات الحساسة.
          </li>
          <li>
            <b>الالتزام النظامي</b>: حيثما تكون المعالجة ضرورية للامتثال للأنظمة
            واللوائح السارية في المملكة العربية السعودية، بما فيها متطلبات
            الضرائب ومكافحة غسل الأموال والتقارير التنظيمية.
          </li>
          <li>
            <b>المصلحة العامة</b>: حيثما تكون المعالجة ضرورية لأغراض المصلحة
            العامة وفقًا لما هو محدّد في نظام حماية البيانات الشخصية.
          </li>
        </ul>
        <p>
          ولكل نشاط معالجة، نحدّد الأساس النظامي المنطبق ونوثّقه قبل بدء
          المعالجة، ونعتمد على أساس واحد لكل غرض وفقًا لما هو موثَّق في سجلات
          أنشطة المعالجة لدينا. وحيثما تستند المعالجة إلى موافقتك، تُطلَب تلك
          الموافقة بشكل منفصل لكل غرض مستقل، وتُوثَّق وتُسجَّل، ويجوز لك سحبها
          في أي وقت بأثر مستقبلي من خلال إعدادات حسابك أو بالتواصل معنا؛ ولا
          يؤثر سحب الموافقة في مشروعية المعالجة التي تمت قبل السحب، وعند سحب
          الموافقة نتوقف عن المعالجة المعنية ما لم ينطبق أساس نظامي آخر بموجب
          نظام حماية البيانات الشخصية وبالقدر الذي ينطبق فيه، ونخطرك بذلك.
        </p>
      </section>

      <section id="purposes">
        <h2><span class="num">5</span> أغراض المعالجة</h2>
        <p>
          نعالج البيانات الشخصية للأغراض المحدّدة والواضحة والصريحة التالية:
        </p>
        <ul>
          <li>
            توفير منصة شركة الخبرات الصاعدة لتقنية المعلومات وخدماتها وتشغيلها، بما في ذلك البحث عن الكفاءات
            والمقابلات والتقييمات وإدارة التوظيف والتقديم التلقائي وتحسين السيرة
            الذاتية
          </li>
          <li>إنشاء حسابات المستخدمين وإدارتها</li>
          <li>معالجة المدفوعات وإصدار الفواتير</li>
          <li>تمكين البحث في ملفات المرشحين وتصفيتها وترتيبها وفتحها</li>
          <li>تقديم نتائج مطابقة وترتيب المرشحين المدعومة بالذكاء الاصطناعي</li>
          <li>
            مطابقة الباحثين عن عمل مع الوظائف المناسبة وإنشاء نسخ مخصَّصة من
            سيرهم الذاتية وتقديم طلبات التوظيف نيابةً عنهم (التقديم التلقائي)
          </li>
          <li>
            إدراج ملفات الباحثين عن عمل وسيرهم الذاتية، بما فيها السير الذاتية
            المرفوعة لمعاينة مطابقات الوظائف المتاحة أو التحقق من عددها، في
            قاعدة بيانات المرشحين وقاعدة الكفاءات لدى شركة الخبرات الصاعدة لتقنية المعلومات، وإتاحتها وجعلها
            قابلة للاكتشاف لأصحاب العمل وجهات التوظيف عبر البحث عن الكفاءات
            وغيره من خدمات شركة الخبرات الصاعدة لتقنية المعلومات الحالية أو المستقبلية
          </li>
          <li>
            تحسين السير الذاتية للباحثين عن عمل وإعادة صياغتها (تحسين السيرة
            الذاتية)
          </li>
          <li>تحليل مقابلات المرشحين وتقييماتهم وتقويمها</li>
          <li>تقديم توصيات التوظيف والرؤى المولَّدة بالذكاء الاصطناعي</li>
          <li>تخصيص تجربتك مع شركة الخبرات الصاعدة لتقنية المعلومات</li>
          <li>
            التواصل مع المستخدمين بشأن حساباتهم وفوترتهم وطلباتهم وتحديثات
            الخدمة
          </li>
          <li>
            إرسال الاتصالات التسويقية (مع إمكانية إلغاء الاشتراك في أي وقت)
          </li>
          <li>تحليل استخدام المنصة لتحسين خدماتنا وميزاتنا وتجربة المستخدم</li>
          <li>منع الاحتيال والوصول غير المصرّح به وضمان أمن المنصة</li>
          <li>
            الامتثال للالتزامات القانونية والتنظيمية والحكومية بموجب أنظمة
            المملكة
          </li>
          <li>
            الاستجابة لطلبات أصحاب البيانات وفقًا لنظام حماية البيانات الشخصية
          </li>
        </ul>
        <p>
          ولا نعالج البيانات الشخصية إلا للأغراض المبيَّنة أعلاه وللأغراض
          المتوافقة معها، ولا نعالجها لاحقًا بطريقة لا تتوافق مع تلك الأغراض دون
          تزويدك أولًا بإشعار، والحصول على موافقتك أو تحديد أساس نظامي آخر حيثما
          يقتضي نظام حماية البيانات الشخصية ذلك. وحيثما نعتزم معالجة البيانات
          الشخصية لغرض جديد، نقيّم العلاقة بين الغرض الأصلي والغرض الجديد، وسياق
          الجمع، وطبيعة البيانات، والآثار المحتملة عليك، ووجود الضمانات
          المناسبة، قبل بدء تلك المعالجة.
        </p>
      </section>

      <section id="collection-methods">
        <h2><span class="num">6</span> طرق جمع البيانات</h2>
        <ul>
          <li>
            <b>الجمع المباشر</b>: المعلومات التي تقدّمها عند إنشاء حساب أو رفع
            سيرة ذاتية أو تحديد التفضيلات الوظيفية أو الاشتراك في باقة أو تقديم
            طلب تحسين سيرة ذاتية أو التواصل مع فريق الدعم.
          </li>
          <li>
            <b>الجمع الآلي</b>: بيانات الاستخدام وملفات تعريف الارتباط وتقنيات
            التتبع المماثلة التي تُجمَع تلقائيًا عند تفاعلك مع المنصة.
          </li>
          <li>
            <b>البيانات المولَّدة</b>: البيانات التي تنشئها أنظمتنا في سياق
            تقديم الخدمات، مثل السير الذاتية المخصَّصة ونتائج المطابقة وتقييمات
            الذكاء الاصطناعي.
          </li>
          <li>
            <b>مصادر الأطراف الثالثة</b>: بيانات ملفات المرشحين المستمدة من
            قواعد بيانات مهنية متاحة للعامة وشركاء بيانات معتمَدين وتقديمات
            المرشحين المباشرة.
          </li>
        </ul>
        <p>
          وحيثما تُجمَع البيانات الشخصية بشكل غير مباشر (ليس من صاحب البيانات
          مباشرةً)، نتخذ التدابير اللازمة لإبلاغ صاحب البيانات خلال ثلاثين (30)
          يومًا أو عند أول تواصل معه، أيهما أسبق، وفقًا للوائح التنفيذية لنظام
          حماية البيانات الشخصية.
        </p>
      </section>

      <section id="cookies">
        <h2><span class="num">7</span> ملفات تعريف الارتباط وتقنيات التتبع</h2>
        <p>
          نستخدم ملفات تعريف الارتباط وتقنيات مماثلة لتحسين تجربتك وتحليل
          استخدام المنصة. وتشمل:
        </p>
        <ul>
          <li>
            <b>ملفات تعريف الارتباط الأساسية</b>: ضرورية لعمل المنصة والمصادقة
            والأمن، ولا يمكن تعطيلها.
          </li>
          <li>
            <b>ملفات تعريف الارتباط التحليلية</b>: تُستخدَم لفهم كيفية تفاعل
            الزوار مع المنصة، بما يساعدنا على تحسين الأداء وتجربة المستخدم.
          </li>
          <li>
            <b>ملفات تعريف الارتباط التسويقية</b>: تُستخدَم لعرض إعلانات ملائمة
            وقياس فاعلية الحملات، ولا تُوضَع إلا بموافقتك.
          </li>
        </ul>
        <p>
          يمكنك إدارة تفضيلات ملفات تعريف الارتباط عبر إعدادات متصفحك أو عبر
          شريط الموافقة المعروض على المنصة. وقد يؤثر تعطيل بعض الملفات في عمل
          المنصة.
        </p>
      </section>

      <section id="sharing">
        <h2><span class="num">8</span> مشاركة البيانات والإفصاح عنها</h2>
        <div class="callout">نحن لا نبيع البيانات الشخصية.</div>
        <p>
          قد نشارك البيانات الشخصية مع فئات المستلمين التالية، وذلك حصرًا
          للأغراض الموضّحة في هذه السياسة:
        </p>
        <ul>
          <li>
            <b>أصحاب العمل ومنصات التوظيف (التقديم التلقائي)</b>: عندما يقدّم
            التقديم التلقائي طلبًا نيابةً عنك، يُفصَح عن بيانات طلبك وسيرتك
            الذاتية المخصَّصة، بما تتضمنه من بيانات شخصية، لصاحب العمل المحتمل
            أو منصة التوظيف التي تستضيف الوظيفة. وهذا الإفصاح هو الوظيفة
            الجوهرية للخدمة التي تشترك فيها.
          </li>
          <li>
            <b>المشتركون (البحث عن الكفاءات)</b>: يُفصَح عن بيانات تواصل المرشح
            (البريد الإلكتروني ورقم الهاتف) للمشتركين فقط عند فتح الملف، وفي
            حدود مخصّص باقة المشترك. ويجوز إدراج ملفات الباحثين عن عمل وسيرهم
            الذاتية المقدَّمة عبر أي خدمة من خدمات شركة الخبرات الصاعدة لتقنية المعلومات، بما فيها السير الذاتية
            المرفوعة لمعاينة مطابقات الوظائف المتاحة أو التحقق من عددها، في
            قاعدة بيانات المرشحين القابلة للبحث من قبل المشتركين والمعروضة
            عليهم، مع عدم الإفصاح عن بيانات التواصل إلا عند فتح الملف.
          </li>
          <li>
            <b>مزوّدو الخدمات</b>: مزوّدون من أطراف ثالثة يساعدون في معالجة
            المدفوعات والاستضافة السحابية والتحليلات وتوصيل البريد الإلكتروني
            والمراسلة ودعم العملاء. وجميع مزوّدي الخدمات ملزَمون تعاقديًا بحماية
            البيانات الشخصية وفقًا لنظام حماية البيانات الشخصية.
          </li>
          <li>
            <b>الشركاء والشركات التابعة</b>: لا نشارك بيانات شخصية مع شركائنا
            وشركاتنا التابعة لأغراض تسويقية إلا بموافقتك المسبقة. ويمكنك سحب هذه
            الموافقة في أي وقت.
          </li>
          <li>
            <b>الجهات القانونية والتنظيمية</b>: حيثما يكون الإفصاح مطلوبًا بموجب
            نظام أو لائحة أو أمر قضائي أو طلب حكومي صادر عن الجهات المختصة في
            المملكة العربية السعودية.
          </li>
          <li>
            <b>عمليات نقل الأعمال</b>: في سياق اندماج أو استحواذ أو بيع كل
            أصولنا أو معظمها، شريطة التزام الكيان المستحوذ باحترام سياسة
            الخصوصية هذه.
          </li>
        </ul>
        <p>
          وحيثما يقيّد نظام حماية البيانات الشخصية الإفصاح، كما في الحالات التي
          قد يهدّد فيها الإفصاح الأمن الوطني أو سلامة الأفراد، فإننا نلتزم بتلك
          القيود.
        </p>
        <p>
          ويعمل جميع مزوّدي الخدمات والمعالجين الذين يعالجون بيانات شخصية نيابةً
          عنا بموجب اتفاقيات كتابية تفرض التزامات بالسرية والأمن وتحديد الغرض
          والامتثال لنظام حماية البيانات الشخصية، وتقصر المعالجة على تعليماتنا
          الموثَّقة، وتوجب إعادة البيانات الشخصية أو إتلافها عند انتهاء التعاقد.
          ولا نبيع البيانات الشخصية، ولا نسمح لمعالجينا باستخدامها لأغراضهم
          المستقلة. وتقتصر الإفصاحات لأصحاب العمل وجهات التوظيف عبر الخدمات على
          البيانات المتاحة من خلال ميزات المنتج المعنية، وتخضع لالتزامات المتلقي
          بموجب أنظمة حماية البيانات السارية.
        </p>
      </section>

      <section id="transfers">
        <h2><span class="num">9</span> نقل البيانات خارج المملكة</h2>
        <p>
          تخزّن شركة الخبرات الصاعدة لتقنية المعلومات البيانات الشخصية وتعالجها على بنية تحتية سحابية آمنة
          يشغّلها مزوّدو خدمات سحابية رائدون. وحيثما تُخزَّن البيانات الشخصية أو
          تُعالَج أو يجري الوصول إليها خارج المملكة العربية السعودية، أو تُنقَل
          خارج المملكة بأي شكل آخر (بما في ذلك عندما يُقدَّم طلب تقديم تلقائي
          إلى صاحب عمل خارج المملكة بتوجيه منك)، فإننا نضمن ما يلي:
        </p>
        <ul>
          <li>
            أن يخدم النقل غرضًا محدّدًا ومشروعًا وفقًا لما يسمح به نظام حماية
            البيانات الشخصية.
          </li>
          <li>
            أن توفّر الدولة أو الجهة المستلمة مستوى ملائمًا من حماية البيانات
            الشخصية وفق تقييم سدايا، أو أن تكون هناك ضمانات مناسبة قائمة.
          </li>
          <li>
            استخدام آليات نقل مناسبة، بما فيها البنود التعاقدية القياسية أو
            القواعد الملزمة للشركات حيثما يُطلَب.
          </li>
          <li>
            استمرار انطباق الحماية المقرّرة بموجب نظام حماية البيانات الشخصية
            على البيانات المنقولة.
          </li>
        </ul>
        <p>
          وقبل أي نقل للبيانات خارج المملكة، نقيّم ونوثّق ضرورة النقل وفئات
          البيانات المعنية والوجهة والضمانات المطبَّقة، ولا ننقل إلا الحد الأدنى
          من البيانات الشخصية اللازم لتحقيق غرض النقل. وحيثما تُصدِر الهيئة
          السعودية للبيانات والذكاء الاصطناعي (سدايا) قرارات كفاية أو قواعد نقل
          أو إرشادات أو تعدّلها أو تسحبها، نوائم ممارسات النقل لدينا مع
          المتطلبات السارية وقت النقل.
        </p>
      </section>

      <section id="retention">
        <h2><span class="num">10</span> الاحتفاظ بالبيانات</h2>
        <p>
          نحتفظ بالبيانات الشخصية فقط للمدة اللازمة لتحقيق الأغراض التي جُمعت من
          أجلها، أو وفقًا لما يقتضيه النظام. وممارساتنا في الاحتفاظ كما يلي:
        </p>
        <ul>
          <li>
            <b>بيانات الحساب (المشتركون والباحثون عن عمل)</b>: يُحتفَظ بها طوال
            مدة الحساب أو الاشتراك النشط ولمدة خمس (5) سنوات بعد إغلاق الحساب،
            وفقًا لمتطلبات حفظ السجلات والامتثال النظامي.
          </li>
          <li>
            <b>بيانات الدفع والمعاملات</b>: يُحتفَظ بها للمدة الدنيا التي
            تقتضيها الأنظمة الضريبية والتجارية السعودية.
          </li>
          <li>
            <b>بيانات ملفات المرشحين</b>: يُحتفَظ بها ما دامت البيانات ملائمة
            ودقيقة، مع مراجعة دورية. ويمكن للمرشحين طلب الحذف في أي وقت.
          </li>
          <li>
            <b
              >بيانات السيرة الذاتية والطلبات والسير المخصَّصة (التقديم التلقائي
              وتحسين السيرة الذاتية)</b
            >: يُحتفَظ بها طوال مدة حسابك وبالقدر اللازم لإثبات الطلبات
            المقدَّمة نيابةً عنك؛ ويمكنك طلب الحذف في أي وقت، مع مراعاة متطلبات
            حفظ السجلات النظامية. أما نسخ الطلبات التي سُلِّمت بالفعل إلى أصحاب
            العمل فتُحفَظ لديهم وفق سياساتهم الخاصة.
          </li>
          <li>
            <b>بيانات الاستخدام والتحليلات</b>: يُحتفَظ بها بصيغة مجهولة الهوية
            أو مجمَّعة لأغراض تحسين الخدمة.
          </li>
          <li>
            <b>سجلات أنشطة المعالجة</b>: يُحتفَظ بها طوال مدة أنشطة المعالجة
            ولمدة خمس (5) سنوات إضافية بعدها، وفقًا للوائح التنفيذية لنظام حماية
            البيانات الشخصية.
          </li>
        </ul>
        <p>
          وعندما لا تعود البيانات الشخصية لازمة، نُتلفها بأمان أو نجعلها مجهولة
          الهوية بحيث يتعذّر إعادة التعرف على صاحب البيانات.
        </p>
        <p>
          وعند تحديد مدد الاحتفاظ، نطبّق المعايير التالية: مدة العلاقة التعاقدية
          وأي مدد ضمان أو نزاع أو تقادم منطبقة؛ والتزامات الاحتفاظ النظامية
          بموجب أنظمة المملكة، بما فيها متطلبات حفظ السجلات الضريبية والتجارية
          والتنظيمية؛ والحاجة التشغيلية المشروعة إلى البيانات، بما في ذلك أغراض
          الأمن والتدقيق ومنع الاحتيال؛ وطبيعة البيانات المعنية ودرجة حساسيتها.
          وحيثما يُحتفَظ بالبيانات لأغراض مختلفة بمدد مختلفة، لا يُحتفَظ بها إلا
          للمدة الأطول المنطبقة، ويقتصر استخدامها على الأغراض المبرِّرة لتلك
          المدة.
        </p>
      </section>

      <section id="security">
        <h2><span class="num">11</span> أمن البيانات</h2>
        <p>
          نطبّق تدابير تقنية وتنظيمية مناسبة لحماية البيانات الشخصية من الوصول
          غير المصرّح به أو التعديل أو الإفصاح أو الإتلاف. وتشمل هذه التدابير،
          على سبيل المثال لا الحصر:
        </p>
        <ul>
          <li>
            تشفير البيانات الشخصية أثناء النقل (<span class="en">TLS/SSL</span>)
            وفي حالة التخزين
          </li>
          <li>
            بنية تحتية سحابية محصَّنة ومقيَّدة الوصول يشغّلها مزوّدو خدمات
            سحابية رائدون
          </li>
          <li>
            ضوابط وصول قائمة على الأدوار ومصادقة متعددة العوامل للأنظمة الداخلية
          </li>
          <li>تقييمات أمنية دورية وفحص للثغرات واختبارات اختراق</li>
          <li>تدريب الموظفين على حماية البيانات واتفاقيات سرية</li>
          <li>
            إجراءات استجابة للحوادث متوافقة مع إرشادات مكتب إدارة البيانات
            الوطنية وسدايا
          </li>
          <li>إطار حوكمة بيانات متوافق مع مكتب إدارة البيانات الوطنية</li>
        </ul>
        <p>
          وبينما نتخذ احتياطات معقولة تجاريًا، لا توجد وسيلة نقل أو تخزين
          إلكتروني آمنة تمامًا. وفي حال وقوع خرق للبيانات الشخصية، سنُخطر سدايا
          خلال اثنتين وسبعين (72) ساعة ونبلغ أصحاب البيانات المتأثرين دون تأخير
          غير مبرَّر، وفقًا لما يقتضيه نظام حماية البيانات الشخصية.
        </p>
        <p>
          وأنت المسؤول عن الحفاظ على سرية بيانات الدخول إلى حسابك، وعن استخدام
          أجهزة وشبكات مؤمَّنة بشكل مناسب عند الوصول إلى الخدمات، وعن إخطارنا
          فورًا بأي اشتباه في اختراق حسابك. وتُراجَع التدابير الأمنية دوريًا
          وتُعدَّل في ضوء التهديدات المتغيرة والتطورات التقنية والإرشادات
          التنظيمية؛ غير أنه لا يمكن لأي مجموعة من التدابير ضمان أمن مطلق، وتتم
          عمليات الإرسال من الخدمات وإليها بمبادرة منك.
        </p>
      </section>

      <section id="rights">
        <h2>
          <span class="num">12</span> حقوقك بموجب نظام حماية البيانات الشخصية
        </h2>
        <p>
          وفقًا لنظام حماية البيانات الشخصية ولوائحه التنفيذية، تتمتع بالحقوق
          التالية فيما يتعلق ببياناتك الشخصية:
        </p>
        <ul>
          <li>
            <b>الحق في العلم</b>: معرفة هويتنا والغرض من جمع بياناتك وطرق الجمع
            والجهات التي قد تُشارَك معها بياناتك.
          </li>
          <li>
            <b>الحق في الوصول</b>: طلب الوصول إلى البيانات الشخصية التي نحتفظ
            بها عنك والحصول على نسخة منها.
          </li>
          <li>
            <b>الحق في التصحيح</b>: طلب تصحيح أي بيانات شخصية غير دقيقة أو غير
            مكتملة أو قديمة.
          </li>
          <li>
            <b>الحق في الإتلاف</b>: طلب إتلاف بياناتك الشخصية عندما لا تعود
            لازمة للغرض الذي جُمعت من أجله، أو عند سحبك لموافقتك.
          </li>
          <li>
            <b>الحق في سحب الموافقة</b>: سحب موافقتك على المعالجة في أي وقت. ولا
            يؤثر السحب في مشروعية المعالجة التي تمت قبله، ولا يسترجع الطلبات
            التي سبق تقديمها إلى أصحاب العمل بناءً على تعليماتك.
          </li>
          <li>
            <b>الحق في تقييد المعالجة</b>: طلب الحد من معالجة بياناتك في ظروف
            معيّنة ولمدة محدودة.
          </li>
          <li>
            <b>الحق في نقل البيانات</b>: الحصول على بياناتك الشخصية بصيغة
            منظَّمة وشائعة الاستخدام وقابلة للقراءة آليًا، وطلب نقلها إلى جهة
            تحكم أخرى.
          </li>
          <li>
            <b>الحق في الاعتراض</b>: الاعتراض على المعالجة القائمة على المصلحة
            المشروعة.
          </li>
        </ul>
        <p>
          لممارسة أي من هذه الحقوق، تواصل معنا عبر
          <a href="mailto:support@uptal.com" class="mail en"
            >support@uptal.com</a
          >
          أو عبر
          <a routerLink="/contact-us" class="en">uptal.com/contact-us</a>. وسنرد
          خلال ثلاثين (30) يومًا تقويميًا. وفي الحالات المعقّدة، قد تُمدَّد هذه
          المدة ثلاثين (30) يومًا إضافية، وسنخطرك بالتمديد وسببه. وقد نطلب
          التحقق من الهوية قبل معالجة طلبك.
        </p>
        <p>
          وممارسة حقوقك مجانية، غير أنه يجوز لنا، حيثما كانت الطلبات لا أساس لها
          بشكل ظاهر أو مفرطة أو متكررة، فرض رسوم إدارية معقولة أو رفض التصرف
          بناءً على الطلب، وذلك في كل حالة بالقدر الذي يجيزه نظام حماية البيانات
          الشخصية، وسنوضح سبب أي قرار من هذا القبيل. وتخضع بعض الحقوق لشروط
          واستثناءات بموجب النظام ولوائحه التنفيذية، بما في ذلك حيثما يمسّ تنفيذ
          الطلب حقوق الآخرين، أو يتعارض مع التزام نظامي، أو يتعلق ببيانات
          تُعالَج لأغراض مستثناة نظامًا؛ وحيثما نعتمد على شرط أو استثناء من هذا
          القبيل، سنخطرك بذلك ما لم يكن ذلك محظورًا علينا. وإذا لم تكن راضيًا عن
          معالجتنا لطلبك، فيمكنك تصعيد المسألة إلى جهة التواصل لحماية البيانات
          المحدَّدة في هذه السياسة، ويحق لك، دون إخلال بذلك، تقديم شكوى إلى
          الجهة المختصة وفقًا لما هو موضح في قسم الشكاوى.
        </p>
      </section>

      <section id="sensitive">
        <h2><span class="num">13</span> البيانات الشخصية الحساسة</h2>
        <p>
          لا تجمع شركة الخبرات الصاعدة لتقنية المعلومات أو تعالج عمدًا بيانات شخصية حساسة وفقًا لتعريفها في نظام
          حماية البيانات الشخصية، وتشمل البيانات الصحية والبيانات الحيوية
          والبيانات الوراثية والبيانات الكاشفة عن الأصل العرقي أو الإثني
          والسجلات الجنائية. وإذا كانت سيرتك الذاتية تتضمن مثل هذه المعلومات،
          فنوصي بإزالتها قبل الرفع. وإذا علمنا بجمع بيانات حساسة دون قصد، فسنتخذ
          فورًا خطوات لحذفها أو الحصول على الموافقة الصريحة المطلوبة.
        </p>
      </section>

      <section id="children">
        <h2><span class="num">14</span> بيانات القاصرين</h2>
        <p>
          شركة الخبرات الصاعدة لتقنية المعلومات غير موجّهة للأفراد دون سن الثامنة عشرة (18). ولا نجمع عن علم
          بيانات شخصية من القاصرين. وإذا علمنا بجمع بيانات من قاصر، فسنتخذ خطوات
          فورية لحذفها.
        </p>
      </section>

      <section id="marketing">
        <h2><span class="num">15</span> التسويق المباشر</h2>
        <p>
          قد نستخدم بيانات التواصل الخاصة بك لإرسال اتصالات تسويقية حول منتجات
          شركة الخبرات الصاعدة لتقنية المعلومات وميزاتها وعروضها. ويمكنك إلغاء الاشتراك في أي وقت عبر:
        </p>
        <ul>
          <li>النقر على رابط إلغاء الاشتراك في أي بريد تسويقي</li>
          <li>
            التواصل معنا عبر
            <a href="mailto:support@uptal.com" class="mail en"
              >support@uptal.com</a
            >
          </li>
          <li>تحديث تفضيلات الاتصال في إعدادات حسابك</li>
        </ul>
        <p>
          ولا يؤثر إلغاء الاشتراك في الاتصالات التسويقية على الاتصالات المتعلقة
          بالمعاملات أو الخدمة (مثل إشعارات الفوترة وتحديثات الطلبات والتنبيهات
          الأمنية وتأكيدات الاشتراك).
        </p>
      </section>

      <section id="ai">
        <h2><span class="num">16</span> القرارات الآلية والذكاء الاصطناعي</h2>
        <p>
          تستخدم شركة الخبرات الصاعدة لتقنية المعلومات الذكاء الاصطناعي والمعالجة الآلية عبر خدماتها، بما في
          ذلك:
        </p>
        <ul>
          <li>
            ترتيب ومطابقة ملفات المرشحين في البحث عن الكفاءات وفق معايير مثل
            تطابق المهارات وملاءمة الراتب ومستوى الأقدمية وملاءمة الدور
          </li>
          <li>
            مطابقة الباحثين عن عمل مع الوظائف في التقديم التلقائي بناءً على
            سيرهم الذاتية والتفضيلات التي يحدّدونها، وإنشاء نسخ مخصَّصة من سيرهم
            الذاتية لكل وظيفة، وتقديم الطلبات نيابةً عنهم
          </li>
          <li>تحليل تسجيلات مقابلات المرشحين وتقييم إجابات التقييمات</li>
          <li>توليد توصيات التوظيف والرؤى المولَّدة بالذكاء الاصطناعي</li>
        </ul>
        <p>
          تساعد هذه العمليات الآلية جهات التوظيف على تحديد المرشحين المناسبين،
          وتساعد الباحثين عن عمل على التقديم على نطاق واسع. والنتائج الآلية، بما
          فيها ترتيب المرشحين وتقييمات المقابلات ودرجات التقييمات ومطابقات
          الوظائف، هي توصيات فقط ولا تشكّل قرارات ملزِمة. والمخرجات المولَّدة
          بالذكاء الاصطناعي تقديرات احتمالية وقد تتضمن أخطاء أو عدم دقة، وليست
          مضمونة الدقة أو الاكتمال أو الحداثة. وتبقى قرارات التوظيف بيد أصحاب
          العمل وحدهم. وفي التقديم التلقائي، تتحكم أنت في تفضيلات الاستهداف التي
          توجّه المطابقة، ويمكنك مراجعة سجل طلباتك وتعديل تفضيلاتك أو إيقاف
          الخدمة مؤقتًا في أي وقت. وتُقدَّم الطلبات بموجب تفويضك الصريح ولا يمكن
          سحبها بعد إرسالها؛ ويسري الإيقاف المؤقت على الطلبات المستقبلية فقط.
          وإذا رأيت أن عملية آلية أثّرت سلبًا في حقوقك، فيمكنك التواصل معنا لطلب
          مراجعة.
        </p>
      </section>

      <section id="thirdparty">
        <h2><span class="num">17</span> روابط الأطراف الثالثة</h2>
        <p>
          قد تتضمن منصة شركة الخبرات الصاعدة لتقنية المعلومات روابط لمواقع أو خدمات أطراف ثالثة، بما فيها مواقع
          أصحاب العمل ومنصات التوظيف التي تُقدَّم إليها الطلبات. ونحن غير
          مسؤولين عن ممارسات الخصوصية أو المحتوى لدى تلك المواقع. ونشجّعك على
          مراجعة سياسات الخصوصية لأي خدمة من أطراف ثالثة تتعامل معها.
        </p>
      </section>

      <section id="dpo">
        <h2><span class="num">18</span> جهة التواصل لحماية البيانات</h2>
        <p>
          وفقًا لمتطلبات نظام حماية البيانات الشخصية، عيّنت شركة الخبرات الصاعدة لتقنية المعلومات جهة تواصل
          لشؤون حماية البيانات. ولأي أسئلة أو استفسارات أو طلبات تتعلق بسياسة
          الخصوصية هذه أو بمعالجة بياناتك الشخصية، يُرجى التواصل معنا عبر
          <a href="mailto:support@uptal.com" class="mail en"
            >support@uptal.com</a
          >.
        </p>
      </section>

      <section id="complaints">
        <h2><span class="num">19</span> الشكاوى</h2>
        <p>
          إذا رأيت أن بياناتك الشخصية عولجت بما يخالف نظام حماية البيانات
          الشخصية، فلك الحق في تقديم شكوى إلى:
        </p>
        <ul>
          <li>
            <b>شركة الخبرات الصاعدة لتقنية المعلومات</b>: بالتواصل معنا عبر
            <a href="mailto:support@uptal.com" class="mail en"
              >support@uptal.com</a
            >. وسنحقق في الشكوى ونرد خلال ثلاثين (30) يومًا.
          </li>
          <li>
            <b>سدايا</b>: الهيئة السعودية للبيانات والذكاء الاصطناعي، وهي الجهة
            الإشرافية المختصة بشؤون حماية البيانات في المملكة العربية السعودية.
            ويمكن تقديم الشكاوى عبر المنصة الوطنية لحوكمة البيانات.
          </li>
        </ul>
      </section>

      <section id="law">
        <h2><span class="num">20</span> القانون الحاكم</h2>
        <p>
          تخضع سياسة الخصوصية هذه لأنظمة المملكة العربية السعودية وتُفسَّر وفقًا
          لها، بما في ذلك نظام حماية البيانات الشخصية ولوائحه التنفيذية. ويخضع
          أي نزاع ينشأ بموجب هذه السياسة للاختصاص القضائي الحصري للمحاكم المختصة
          في مدينة الرياض بالمملكة العربية السعودية.
        </p>
      </section>

      <section id="changes">
        <h2><span class="num">21</span> التغييرات على هذه السياسة</h2>
        <p>
          قد نحدّث سياسة الخصوصية هذه من وقت لآخر لتعكس التغييرات في ممارساتنا
          أو التقنية أو المتطلبات النظامية أو عملياتنا التجارية. وعند إجراء
          تغييرات جوهرية، سنقوم بما يلي:
        </p>
        <ul>
          <li>نشر السياسة المحدَّثة على موقعنا مع تاريخ سريان معدَّل</li>
          <li>
            إخطار المستخدمين النشطين عبر البريد الإلكتروني بالتغييرات المهمة
          </li>
          <li>
            الحصول على موافقتك، حيثما يقتضي نظام حماية البيانات الشخصية ذلك، قبل
            تطبيق تغييرات جوهرية تمسّ طريقة معالجة بياناتك الشخصية
          </li>
        </ul>
        <p>
          ونشجّعك على مراجعة سياسة الخصوصية هذه دوريًا. وفيما عدا الحالات التي
          يقتضي فيها نظام حماية البيانات الشخصية الحصول على موافقتك، يُعدّ
          استمرارك في استخدام شركة الخبرات الصاعدة لتقنية المعلومات بعد أي تغييرات قبولًا للسياسة المعدَّلة.
        </p>
        <p class="lastline">آخر تحديث لسياسة الخصوصية هذه في 30 يوليو 2026.</p>
      </section>
    </article>
  </div>
</div>

<main-footer></main-footer>
